import type { WeaponId } from '@cuboom/shared';

/**
 * Procedural weapon icons for the toolbar — Canvas 2D, zero asset files.
 * Emoji stand-ins lie about color (a 💣 reads black, a pomegranate is red),
 * so every fruit draws itself in its true palette at 32×32.
 */
export function drawWeaponIcon(cv: HTMLCanvasElement, id: WeaponId, size = 32): void {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  cv.width = Math.round(size * dpr);
  cv.height = Math.round(size * dpr);
  cv.style.width = `${size}px`;
  cv.style.height = `${size}px`;
  const ctx = cv.getContext('2d');
  if (!ctx) return;
  // All cases draw in a 32×32 design grid; scale the grid up for previews.
  ctx.scale((size * dpr) / 32, (size * dpr) / 32);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  const circle = (x: number, y: number, r: number, fill: string, stroke?: string): void => {
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    if (stroke) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  };
  const ellipse = (
    x: number, y: number, rx: number, ry: number, rot: number, fill: string, stroke?: string,
  ): void => {
    ctx.beginPath();
    ctx.ellipse(x, y, rx, ry, rot, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    if (stroke) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  };

  switch (id) {
    case 'pomegranate': {
      // Deep red rind, darker rim, the little crown, two seed glints.
      circle(16, 17.5, 11, '#d8324c', '#8f1f34');
      ctx.fillStyle = '#8f1f34';
      for (const dx of [-4, 0, 4]) {
        ctx.beginPath();
        ctx.moveTo(16 + dx - 2, 8);
        ctx.lineTo(16 + dx, 3.5);
        ctx.lineTo(16 + dx + 2, 8);
        ctx.closePath();
        ctx.fill();
      }
      circle(12, 14, 2.4, '#ff7186');
      circle(19, 20, 2, '#ff7186');
      circle(14, 22, 1.6, '#ff8d9d');
      break;
    }
    case 'grapes': {
      // The in-flight cluster, shrunk to the button: the same bottom-heavy
      // bunch of glossy berries under a curled stem and one vine leaf.
      ctx.save();
      ctx.translate(16, 16);
      ctx.scale(0.9, 0.9);
      ctx.translate(0, 2.75); // bunch bbox center (berries y −9..12) → icon center
      const berries: [number, number, number][] = [
        // Upper row first so the lower berries overlap it — bunch depth.
        [-5.8, -3.4, 4.3],
        [0, -4.6, 4.5],
        [5.8, -3.4, 4.3],
        [-7.4, 3, 3.9],
        [7.4, 3, 3.9],
        [-3.4, 2.4, 4.7],
        [3.4, 2.4, 4.7],
        [0, 7.4, 4.6],
      ];
      for (const [bx, by, br] of berries) {
        const gg = ctx.createRadialGradient(bx - 1.6, by - 1.8, 0.6, bx, by, br);
        gg.addColorStop(0, '#a487e0');
        gg.addColorStop(0.55, '#7a4fb5');
        gg.addColorStop(1, '#4d2f7d');
        ctx.fillStyle = gg;
        ctx.beginPath();
        ctx.arc(bx, by, br, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(45,25,80,.7)';
        ctx.lineWidth = 1.2;
        ctx.stroke();
        ctx.fillStyle = 'rgba(255,255,255,.66)';
        ctx.beginPath();
        ctx.ellipse(bx - br * 0.34, by - br * 0.4, br * 0.3, br * 0.2, -0.5, 0, Math.PI * 2);
        ctx.fill();
      }
      // Stem stub + curling tendril.
      ctx.strokeStyle = '#7a5a2e';
      ctx.lineWidth = 2.4;
      ctx.beginPath();
      ctx.moveTo(0, -7.5);
      ctx.quadraticCurveTo(0.6, -11, -1.4, -13.5);
      ctx.stroke();
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(-1.4, -13.5);
      ctx.quadraticCurveTo(3.5, -15.5, 4.5, -12.8);
      ctx.quadraticCurveTo(5.2, -11, 3.4, -11.2);
      ctx.stroke();
      // One vine leaf.
      ctx.fillStyle = '#4fae5c';
      ctx.beginPath();
      ctx.moveTo(-1, -12.4);
      ctx.quadraticCurveTo(-7, -17.5, -11, -14.4);
      ctx.quadraticCurveTo(-7.5, -11, -1, -12.4);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = 'rgba(38,104,50,.8)';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-1.6, -12.5);
      ctx.lineTo(-9.4, -14.4);
      ctx.stroke();
      ctx.restore();
      break;
    }
    case 'watermelon': {
      circle(16, 16, 12, '#3fa34d', '#1f6f31');
      circle(16, 16, 8.5, '#e04f5a');
      ctx.fillStyle = '#241a16';
      for (const [sx, sy] of [[-3, -2], [3, -1], [0, 4]]) {
        ctx.beginPath();
        ctx.ellipse(16 + sx, 16 + sy, 1.3, 2, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case 'coconut': {
      circle(16, 16, 12, '#8a5a2b', '#5c3a19');
      ctx.fillStyle = '#3d2712';
      for (const [hx, hy] of [[13, 13], [19, 13], [16, 18]]) {
        ctx.beginPath();
        ctx.arc(hx, hy, 2, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case 'bamboo': {
      // A cut stalk, leaning like a launched thorn: lit crown, node rings, a
      // pale cut face with pith at the top, and one leaf sprig off the base.
      ctx.save();
      ctx.translate(16, 16);
      ctx.rotate(-0.5);
      // Stalk body — vertical gradient inside a darker outline.
      ctx.fillStyle = '#3f7a31';
      ctx.beginPath();
      ctx.roundRect(-4.6, -13, 9.2, 26, 3.4);
      ctx.fill();
      const grad = ctx.createLinearGradient(-4.6, 0, 4.6, 0);
      grad.addColorStop(0, '#a8d98f');
      grad.addColorStop(0.45, '#7cb86a');
      grad.addColorStop(1, '#4e8b42');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.roundRect(-3.4, -12.4, 6.8, 25, 2.8);
      ctx.fill();
      // Node rings: proud bands with a hairline joint.
      for (const y of [-4, 5]) {
        ctx.fillStyle = '#3f7a31';
        ctx.beginPath();
        ctx.roundRect(-4.4, y - 1.6, 8.8, 3.2, 1.4);
        ctx.fill();
        ctx.strokeStyle = 'rgba(30,60,24,.75)';
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(-0.9, y - 1.6);
        ctx.lineTo(-0.9, y + 1.6);
        ctx.stroke();
      }
      // Cut face at the top end: pale ring + inner pith.
      ctx.fillStyle = '#cfe8b8';
      ctx.beginPath();
      ctx.ellipse(0, -12.4, 3.4, 2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#3f7a31';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = '#e8f4d8';
      ctx.beginPath();
      ctx.ellipse(0, -12.6, 1.6, 1, 0, 0, Math.PI * 2);
      ctx.fill();
      // Leaf sprig off the lower node.
      ctx.fillStyle = '#5aa344';
      ctx.strokeStyle = '#3f7a31';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.ellipse(7.5, 7, 4.4, 1.9, 0.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(4, 5.4);
      ctx.quadraticCurveTo(6.5, 6.2, 10.5, 8.4);
      ctx.stroke();
      ctx.restore();
      break;
    }
    case 'beans': {
      // Three lobed pods from the launcher.
      const pod = '#7cb86a';
      ellipse(11, 13, 5, 3.4, -0.6, pod, '#4a7a44');
      ellipse(21, 15, 5, 3.4, 0.5, pod, '#4a7a44');
      ellipse(15, 23, 5, 3.4, -0.2, pod, '#4a7a44');
      break;
    }
    case 'hen': {
      ellipse(15, 19, 10, 8.5, 0, '#f6f1e3', '#c9bfa6');
      circle(22, 12, 5.5, '#f6f1e3', '#c9bfa6'); // head
      ctx.fillStyle = '#e05252'; // comb
      for (const [cx, cy] of [[20, 6.5], [23, 5.5], [25.5, 7]]) circle(cx, cy, 2.1, '#e05252');
      ctx.fillStyle = '#f0a53b'; // beak
      ctx.beginPath();
      ctx.moveTo(27, 12);
      ctx.lineTo(31, 13.5);
      ctx.lineTo(27, 15);
      ctx.closePath();
      ctx.fill();
      circle(23.5, 11, 1.2, '#241a16'); // eye
      ctx.strokeStyle = '#e05252'; // wing
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(13, 20, 5, -0.6, 1.4);
      ctx.stroke();
      break;
    }
    case 'beehive': {
      // Skep hive: stacked amber bands with a dark entrance.
      const band = '#e0a53b';
      const line = '#8a5a1b';
      ellipse(16, 20, 11, 5, 0, band, line);
      ellipse(16, 15, 10, 5, 0, band, line);
      ellipse(16, 10, 7.5, 4.5, 0, band, line);
      circle(16, 7, 4, '#c98b2a', line);
      circle(16, 21, 2.4, '#4a2c0c');
      break;
    }
    case 'pigeon': {
      ellipse(15, 18, 10, 7.5, -0.25, '#c9ccd4', '#8f93a0');
      circle(23, 11, 5, '#c9ccd4', '#8f93a0');
      ctx.fillStyle = '#8f93a0'; // wing
      ctx.beginPath();
      ctx.ellipse(13, 18, 6, 4, -0.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#f0a53b'; // beak
      ctx.beginPath();
      ctx.moveTo(27, 11);
      ctx.lineTo(31, 12);
      ctx.lineTo(27, 13.5);
      ctx.closePath();
      ctx.fill();
      circle(24, 10, 1.2, '#241a16');
      break;
    }
    case 'lemon': {
      ellipse(16, 16, 11, 7.5, -0.35, '#e8e04a', '#b3ab1f');
      ctx.fillStyle = '#b3ab1f'; // pip points
      ctx.beginPath();
      ctx.moveTo(5, 20);
      ctx.lineTo(2.5, 18);
      ctx.lineTo(5.5, 16.5);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#3fa34d';
      ctx.beginPath();
      ctx.ellipse(24, 7, 4, 2.2, -0.6, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'chili': {
      ctx.fillStyle = '#d94f23';
      ctx.strokeStyle = '#8f2c11';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(22, 8);
      ctx.bezierCurveTo(26, 14, 24, 24, 12, 27);
      ctx.bezierCurveTo(16, 22, 18, 14, 17, 9);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.strokeStyle = '#3fa34d'; // stem
      ctx.lineWidth = 2.6;
      ctx.beginPath();
      ctx.moveTo(19.5, 9);
      ctx.lineTo(15, 5);
      ctx.stroke();
      break;
    }
    case 'carrot': {
      // Carrot with its leafy top — the mortar's ammunition.
      ctx.save();
      ctx.translate(16, 17);
      ctx.rotate(-0.7);
      ctx.fillStyle = '#f08a2a';
      ctx.strokeStyle = '#b35f14';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(0, -12);
      ctx.quadraticCurveTo(4.6, 0, 0, 13);
      ctx.quadraticCurveTo(-4.6, 0, 0, -12);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.strokeStyle = 'rgba(179,95,20,.6)';
      ctx.lineWidth = 1;
      for (const y of [-5, 1, 6]) {
        ctx.beginPath();
        ctx.moveTo(-3, y);
        ctx.lineTo(3, y + 1.5);
        ctx.stroke();
      }
      ctx.fillStyle = '#3fa34d';
      for (const [lx, rot] of [[-2.5, -0.4], [0, 0], [2.5, 0.4]]) {
        ctx.beginPath();
        ctx.ellipse(lx, -15, 2, 5, rot, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      break;
    }
  }
}

/**
 * The Jump action icon — a happy kangaroo caught in the full stretch of a
 * leap, side view. One continuous arched-torso silhouette (nose, dip at the
 * neck, back rising to the hip) with tapered limbs over it: ears pinned
 * back, streaming tail, both hind legs trailing, small front arm. The body
 * plan comes from a leaping-kangaroo silhouette; the face keeps the cuteness.
 */
export function drawJumpIcon(cv: HTMLCanvasElement, size = 32): void {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  cv.width = Math.round(size * dpr);
  cv.height = Math.round(size * dpr);
  cv.style.width = `${size}px`;
  cv.style.height = `${size}px`;
  const ctx = cv.getContext('2d');
  if (!ctx) return;
  // Drawn on the same 32×32 design grid as the weapon icons.
  ctx.scale((size * dpr) / 32, (size * dpr) / 32);
  ctx.translate(-1.2, -1.5); // optically center the leap
  // Squeeze the body plan sideways (about the nose) so the tail gets the
  // length it deserves — in the reference the tail is a third of the animal.
  ctx.translate(4, 0);
  ctx.scale(0.88, 1);
  ctx.translate(-4, 0);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  const fur = ctx.createLinearGradient(6, 6, 26, 26);
  fur.addColorStop(0, '#e8a869');
  fur.addColorStop(1, '#c2733a');
  const outline = '#7a4a20';
  const far = '#a8622f'; // off-side limbs, a shade darker for depth

  // Off-side hind leg, tucked behind the body — reads through as a second,
  // darker leg rather than a second blade.
  ctx.fillStyle = far;
  ctx.strokeStyle = outline;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(22.6, 15.8);
  ctx.quadraticCurveTo(21.8, 18.6, 21.4, 21.0);
  ctx.quadraticCurveTo(21.0, 23.2, 20.6, 24.6);
  ctx.quadraticCurveTo(19.0, 25.4, 17.6, 25.8);
  ctx.quadraticCurveTo(19.4, 26.8, 21.4, 26.4);
  ctx.quadraticCurveTo(22.8, 25.4, 23.2, 22.8);
  ctx.quadraticCurveTo(24.2, 19.8, 24.8, 16.8);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Ears — long, pinned back along the neck by the speed. Far ear first.
  ctx.fillStyle = far;
  ctx.beginPath();
  ctx.moveTo(8.8, 12.4);
  ctx.quadraticCurveTo(12.6, 11.2, 16.6, 9.8);
  ctx.quadraticCurveTo(14.6, 12.6, 11.8, 13.8);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = fur;
  ctx.lineWidth = 1.1;
  ctx.beginPath();
  ctx.moveTo(8.6, 10.8);
  ctx.quadraticCurveTo(12.6, 9.0, 17.2, 7.4);
  ctx.quadraticCurveTo(15.2, 10.6, 11.8, 12.2);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Tail — thick at the root, streaming back with the tip flicked up.
  ctx.lineWidth = 1.35;
  ctx.fillStyle = '#c2733a';
  ctx.beginPath();
  ctx.moveTo(24.8, 11.6);
  ctx.quadraticCurveTo(29.0, 11.6, 32.2, 13.6);
  ctx.quadraticCurveTo(34.2, 13.8, 34.4, 11.6); // tip flick up
  ctx.quadraticCurveTo(33.8, 15.8, 29.4, 17.0);
  ctx.quadraticCurveTo(26.0, 16.0, 24.8, 11.6);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // The torso — ONE continuous silhouette: pointed nose → forehead →
  // dip at the neck → back arching up to the hip → round rump → groin
  // crease → hanging belly → deep chest → throat → back to the nose.
  ctx.fillStyle = fur;
  ctx.strokeStyle = outline;
  ctx.lineWidth = 1.3;
  ctx.beginPath();
  ctx.moveTo(4.0, 14.8);
  ctx.quadraticCurveTo(5.4, 11.6, 8.8, 10.5); // snout → forehead
  ctx.quadraticCurveTo(11.8, 9.3, 14.6, 10.3); // neck dip
  ctx.quadraticCurveTo(19.8, 7.3, 24.2, 9.8); // arched back → hip
  ctx.quadraticCurveTo(27.0, 12.0, 26.6, 15.2); // rump
  ctx.quadraticCurveTo(26.2, 17.3, 23.8, 17.5); // hip underside
  ctx.quadraticCurveTo(21.6, 17.6, 20.4, 15.6); // groin crease
  ctx.quadraticCurveTo(17.6, 18.6, 14.4, 18.9); // belly hangs
  ctx.quadraticCurveTo(11.4, 18.4, 9.2, 16.8); // deep chest
  ctx.quadraticCurveTo(7.0, 15.8, 6.0, 15.4); // throat → chin
  ctx.quadraticCurveTo(5.0, 15.2, 4.0, 14.8); // chin → nose tip
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Cream belly patch, kept just inside the outline.
  ctx.fillStyle = '#f6dfc0';
  ctx.beginPath();
  ctx.ellipse(17.0, 17.0, 3.4, 1.25, -0.05, 0, Math.PI * 2);
  ctx.fill();

  // Near hind leg — the power leg: heavy thigh, clear knee, long foot
  // stretched out behind the leap, stopping under the belly (not the chin).
  ctx.fillStyle = fur;
  ctx.strokeStyle = outline;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(20.6, 15.4);
  ctx.quadraticCurveTo(19.4, 19.6, 18.6, 22.4); // thigh front → knee
  ctx.quadraticCurveTo(17.6, 24.4, 16.4, 25.6); // shin front → ankle
  ctx.quadraticCurveTo(13.8, 26.4, 11.4, 27.0); // foot top → toe tip
  ctx.quadraticCurveTo(13.6, 28.0, 16.0, 27.4); // foot bottom → heel
  ctx.quadraticCurveTo(18.0, 26.8, 19.4, 24.2); // shin back → knee
  ctx.quadraticCurveTo(22.4, 20.6, 24.0, 16.6); // thigh back → hip
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Small front arm, hanging from the chest.
  ctx.lineWidth = 1.1;
  ctx.beginPath();
  ctx.moveTo(10.6, 15.6);
  ctx.quadraticCurveTo(11.8, 17.8, 11.2, 19.9);
  ctx.quadraticCurveTo(12.0, 21.1, 13.2, 20.3);
  ctx.quadraticCurveTo(13.8, 18.7, 13.3, 16.4);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Face: dark nose at the point, big glinty eye, rosy cheek, open smile.
  ctx.fillStyle = '#4a3226';
  ctx.beginPath();
  ctx.ellipse(4.5, 14.7, 1.1, 0.85, -0.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#2f2a24';
  ctx.beginPath();
  ctx.arc(7.4, 12.6, 1.75, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.arc(6.8, 12.0, 0.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,130,150,.6)';
  ctx.beginPath();
  ctx.ellipse(6.7, 14.6, 1.5, 1.05, -0.15, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#5c3a26';
  ctx.lineWidth = 0.9;
  ctx.beginPath();
  ctx.moveTo(5.6, 15.9);
  ctx.quadraticCurveTo(6.8, 16.9, 8.2, 16.2);
  ctx.stroke();
}
